/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { AppRouter } from '@gh/common-shared';
import { isMessageBasket } from '@gh/core-messages';
import { DialogResolve, DialogRouteSnapshot, NotificationService, WaitingMode, WaitingService } from '@gh/core-ui';
import { expandObservable, logWarning } from '@gh/core-util';
import { isFunction, isNil, isString } from 'lodash';
import { Observable } from 'rxjs';
import { ContextResolve, ContextRoute, ContextRouteAttachment, ContextRouter } from './context';

function callInitView(service: any, selector: string | ((...args: string[]) => any), ...args: any[]): any {
  if (isFunction(selector)) {
    return selector(service, ...args);
  } else {
    return service[selector](...args);
  }
}

@Injectable()
export class InitContextViewResolver<I> implements ContextResolve<I> {

  constructor(private injector: Injector,
              private waitingService: WaitingService) {

  }

  resolve(route: ContextRoute): Observable<any> | Promise<any> | any {
    const initView = route.config.data['initView'];

    if (initView) {
      const { service, selector } = initView.service
        ? initView
        : { service: initView, selector: void 0 };

      const serviceInstance = this.injector.get(service);
      const ob$ = expandObservable(callInitView(serviceInstance, selector || 'initContextView', route));
      return initView.noWaiting ? ob$ : ob$.pipe(this.waitingService.operator(WaitingMode.Background));
    } else {
      return void 0;
    }
  }
}

@Injectable()
export class ContextableInitViewResolver implements Resolve<any> {

  constructor(private contextRouter: ContextRouter,
              private router: AppRouter,
              private injector: Injector,
              private notificationService: NotificationService) {
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): any {
    const { contextRoute } = route.data;

    if (isNil(contextRoute)) {
      throw new Error('ContextableInitViewResolver: contextRoute was not defined');
    }

    const childRouter = this.contextRouter.createChild(this.injector);
    if (isString(contextRoute)) {
      logWarning('ContextableInitViewResolver: routing to string-based contextRoute is not supported yet');
    } else {
      childRouter.addRoute(contextRoute);
    }
    const outlet = childRouter.createOutlet(ContextRouteAttachment.Router);
    return outlet.navigate(contextRoute.path, { ...route.params, ...route.queryParams })
      .then((success) => {
        if (success) {
          return Promise.resolve({ outlet });
        } else {
          outlet.destroy();
          return Promise.reject('route was forbidden');
        }
      })
      .catch((error) => {
        outlet.destroy();
        if (isMessageBasket(error) && error.has('msg_resource_not_found')) {
          this.router.navigateByUrl('/404');
          return Promise.reject();
        } else {
          this.notificationService.processAll(error);
          return Promise.reject(error);
        }
      });
  }
}

@Injectable()
export class ContextableInitDialogViewResolver implements DialogResolve<any> {

  constructor(private contextRouter: ContextRouter,
              private injector: Injector,
              private notificationService: NotificationService) {
  }

  resolve(route: DialogRouteSnapshot<any>): any {
    const { contextRoute } = route.routeConfig.data;

    if (isNil(contextRoute)) {
      throw new Error('ContextableInitViewResolver: contextRoute was not defined');
    }

    const childRouter = this.contextRouter.createChild(this.injector);
    if (isString(contextRoute)) {
      logWarning('ContextableInitDialogViewResolver: routing to string-based contextRoute is not supported yet');
    } else {
      childRouter.addRoute(contextRoute);
    }
    const outlet = childRouter.createOutlet(ContextRouteAttachment.Dialog);
    return outlet.navigate(contextRoute.path, route.input)
      .then((success) => {
        if (success) {
          return Promise.resolve({ context: { outlet } });
        } else {
          outlet.destroy();
          return Promise.reject('route was forbidden');
        }
      })
      .catch((error) => {
        outlet.destroy();
        this.notificationService.processAll(error);
        return Promise.reject(error);
      });
  }
}
